import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, MapPin } from 'lucide-react'
import { revealDelay } from '@/lib/motion'
import Blueprint, { BLUEPRINT_CORNER } from '@/components/site/blueprint'

import { AREA_LINKS } from '@/content/navigation'

interface AreaLinksProps {
    title?: string
    intro?: string
    serviceName?: string
}

const AreaLinks: FC<AreaLinksProps> = ({ title = "Areas we serve", intro, serviceName }): ReactNode => {
    return (
        <section aria-labelledby='area-links-heading' className='relative isolate overflow-hidden bg-secondary/60'>
            <Blueprint kind='house' className={BLUEPRINT_CORNER} />

            <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
                <div className='flex flex-wrap items-end justify-between gap-6' data-reveal>
                    <div className='max-w-2xl'>
                        <h2 id='area-links-heading' className='text-3xl font-bold sm:text-4xl'>
                            {title}
                        </h2>

                        <p className='mt-3 text-lg text-muted-foreground'>
                            {intro ?? `We're based in Madison and work across North Alabama${serviceName ? `, providing ${serviceName.toLowerCase()} in every area below` : ''}. We do not work in Tennessee.`}
                        </p>
                    </div>

                    <Link href='/service-areas/' className='inline-flex items-center gap-2 text-lg font-bold text-primary underline underline-offset-4'>
                        All areas and towns
                        <ArrowRight className='size-5' aria-hidden='true' />
                    </Link>
                </div>

                <ul className='mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {AREA_LINKS.map((area, index) => (
                        <li key={area.href} data-reveal style={revealDelay((index % 3) * 70)}>
                            <Link
                                href={area.href}
                                className='flex min-h-16 items-center gap-3 rounded-2xl border border-border bg-white px-5 py-4 text-lg font-bold text-navy transition-all hover:-translate-y-0.5 hover:border-primary hover:text-primary'
                            >
                                <MapPin className='size-5 shrink-0 text-primary' aria-hidden='true' />
                                {area.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default AreaLinks
